// src/pages/dashboard/components/RecentActivity.jsx
import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ro } from 'date-fns/locale';
import { 
  FileText, 
  Users, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  XCircle,
  PlayCircle 
} from 'lucide-react';

const RecentActivity = ({ activities = [], isLoading = false }) => {
  const getActivityIcon = (type, status) => {
    if (type === 'Contract') {
      switch (status) {
        case 'Generat':
        case 'PDF generat':
          return <FileText className="w-5 h-5 text-blue-500" />;
        case 'Trimis clientului':
          return <PlayCircle className="w-5 h-5 text-yellow-500" />;
        case 'Semnat':
          return <CheckCircle className="w-5 h-5 text-green-500" />;
        case 'Expirat':
          return <XCircle className="w-5 h-5 text-red-500" />;
        default:
          return <FileText className="w-5 h-5 text-gray-500" />;
      }
    } else if (type === 'CronJob') {
      switch (status) {
        case 'Finalizat':
          return <CheckCircle className="w-5 h-5 text-green-500" />;
        case 'Eșuat':
          return <XCircle className="w-5 h-5 text-red-500" />;
        case 'În execuție':
          return <PlayCircle className="w-5 h-5 text-blue-500" />;
        default:
          return <Clock className="w-5 h-5 text-yellow-500" />;
      }
    }
    return <AlertCircle className="w-5 h-5 text-gray-500" />;
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'Semnat':
      case 'Finalizat':
        return 'bg-green-100 text-green-800';
      case 'Trimis clientului':
      case 'În execuție':
        return 'bg-blue-100 text-blue-800';
      case 'Programat':
        return 'bg-yellow-100 text-yellow-800';
      case 'Expirat':
      case 'Eșuat':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { 
        addSuffix: true, 
        locale: ro 
      });
    } catch (error) {
      return 'Data invalidă';
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Activitate Recentă</h3>
        </div>
        <div className="p-6 space-y-4">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="flex items-center space-x-4 animate-pulse">
              <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Activitate Recentă</h3>
          <span className="text-sm text-gray-500">
            {activities.length} {activities.length === 1 ? 'activitate' : 'activități'}
          </span>
        </div>
      </div>
      
      <div className="max-h-96 overflow-y-auto">
        {activities.length === 0 ? (
          <div className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Nu există activități recente</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {activities.map((activity, index) => (
              <div key={index} className="p-4 hover:bg-gray-50 transition-colors duration-150">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    {getActivityIcon(activity.type, activity.status)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {activity.action}
                      </p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(activity.Status)}`}>
                        {activity.status}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mt-1 truncate">
                      {activity.description}
                    </p>
                    
                    <p className="text-xs text-gray-500 mt-2">
                      {formatDate(activity.date)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {activities.length > 0 && (
        <div className="p-4 border-t border-gray-200 text-center">
          <button className="text-sm text-primary-500 hover:text-primary-600 font-medium">
            Vezi toate activitățile
          </button>
        </div>
      )}
    </div>
  );
};

export default RecentActivity;